import { useSearchParams } from 'next/navigation'
import { ErrorInfo, ReactNode, useCallback, useEffect, useRef } from 'react'
import ErrorBoundary from './ErrorBoundary'
import PreviewUISync from './PreviewUISync'

interface SectionWrapperProps {
  id: string
  sorting?: boolean
  children: ReactNode
}

const SectionWrapper = ({
  id,
  sorting,
  children,
}: SectionWrapperProps): JSX.Element => {
  const searchParams = useSearchParams()
  const containerRef = useRef<HTMLDivElement>(null)
  const mountedRef = useRef(false)

  const getErrorMessage = (params: { error: Error; info: ErrorInfo }) => ({
    ...params.error,
    id,
    type: 'SectionError',
  })

  const renderError = (error?: Error) => {
    return searchParams?.get('debug') === 'true' ? (
      <div data-testid="container.error">
        <p>{`Error rendering section ${id}`}</p>
        <p>{error && JSON.stringify(error.stack)}</p>
      </div>
    ) : null
  }

  const scrollIntoView = useCallback((payload?: { isPopup: boolean }) => {
    if (payload?.isPopup) {
      return
    }

    containerRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    })
  }, [])

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
    } else if (sorting) {
      scrollIntoView()
    }
  }, [scrollIntoView, sorting])

  return (
    <ErrorBoundary
      getErrorMessage={getErrorMessage}
      renderError={renderError}
      state={{ hasError: false, error: null }}
    >
      <div data-testid="container.box" ref={containerRef}>
        <PreviewUISync
          sectionId={id}
          onPreviewModeScrollIntoView={scrollIntoView}
        />
        {children}
      </div>
    </ErrorBoundary>
  )
}

export default SectionWrapper
